import { createRenderer, resizeRenderer, disposeRenderer, type RendererContext } from './createRenderer';
import { createVideoPlane, type VideoPlane } from './videoPlane';
import { createRenderLoop, type RenderLoop } from './renderLoop';
import { FrameDecoder } from '../engine/decoder';
import { ToneAudioEngine } from '../engine/toneAudioEngine';
import type { IAudioEngine } from '../engine/audioEngineInterface';
import { getProxy } from '../engine/proxyManager';
import { useEditorStore } from '../store/editorStore';
import { usePlayheadStore } from '../store/playheadStore';

let ctx: RendererContext | null = null;
let plane: VideoPlane | null = null;
let loop: RenderLoop | null = null;
let decoder: FrameDecoder | null = null;
let audio: IAudioEngine | null = null;
let resizeObserver: ResizeObserver | null = null;
let storeUnsub: (() => void) | null = null;

function triggerResize() {
  if (!ctx) return;
  const parent = ctx.canvas.parentElement ?? ctx.canvas;
  const { clientWidth: w, clientHeight: h } = parent;
  if (w > 0 && h > 0) {
    const aspect = useEditorStore.getState().project.projectAspect;
    resizeRenderer(ctx, w, h, aspect);
  }
}

export function attach(canvas: HTMLCanvasElement) {
  if (ctx) detach();

  ctx = createRenderer(canvas);
  plane = createVideoPlane();
  ctx.scene.add(plane.mesh);

  loop = createRenderLoop(ctx, plane);

  // Auto-resize on container size change
  resizeObserver = new ResizeObserver((entries) => {
    const { width, height } = entries[0].contentRect;
    if (ctx && width > 0 && height > 0) {
      const aspect = useEditorStore.getState().project.projectAspect;
      resizeRenderer(ctx, width, height, aspect);
    }
  });
  resizeObserver.observe(canvas.parentElement ?? canvas);

  // Subscribe to projectAspect changes in the store
  let prevAspect = useEditorStore.getState().project.projectAspect;
  storeUnsub = useEditorStore.subscribe((state) => {
    const newAspect = state.project.projectAspect;
    if (newAspect !== prevAspect) {
      prevAspect = newAspect;
      triggerResize();
    }
  });

  triggerResize();
  loop.start();
}

export function detach() {
  loop?.stop();
  resizeObserver?.disconnect();
  storeUnsub?.();
  decoder?.dispose();
  audio?.dispose();
  plane?.dispose();
  if (ctx) disposeRenderer(ctx);

  ctx = null;
  plane = null;
  loop = null;
  decoder = null;
  audio = null;
  resizeObserver = null;
  storeUnsub = null;
}

/** Ensure the shared audio engine exists (creates if needed) */
function ensureAudioEngine(): ToneAudioEngine {
  if (!audio) {
    audio = new ToneAudioEngine();
    loop?.setAudioEngine(audio);
  }
  return audio as ToneAudioEngine;
}

export async function setActiveAsset(assetId: string) {
  const proxyData = await getProxy(assetId);
  if (!proxyData) throw new Error(`No proxy found for asset ${assetId}`);

  // Show video plane (may have been hidden by clearActiveAsset)
  if (plane) plane.mesh.visible = true;

  // Init video decoder
  decoder?.dispose();
  decoder = new FrameDecoder();
  await decoder.init(proxyData);
  loop?.setDecoder(decoder);

  // Add this asset's audio buffer to the engine (skip if no audio track)
  try {
    const engine = ensureAudioEngine();
    await engine.initAsset(assetId, proxyData);
  } catch {
    // Video has no audio track — video decoder still works
  }
}

/** Init audio engine with an audio-only asset (e.g. recording) — no video decoder */
export async function initAudioAsset(assetId: string, proxyData: ArrayBuffer): Promise<void> {
  const engine = ensureAudioEngine();
  await engine.initAsset(assetId, proxyData);
}

export function clearActiveAsset() {
  // Reset playhead state
  usePlayheadStore.getState().setPlaying(false);
  usePlayheadStore.getState().setCurrentTime(0);

  decoder?.dispose();
  decoder = null;
  audio?.stop?.();
  audio?.dispose();
  audio = null;
  loop?.setDecoder(null);
  loop?.setAudioEngine(null);
  // Hide the video plane and clear the canvas so the last frame doesn't persist
  if (plane) {
    plane.mesh.visible = false;
  }
  if (ctx) {
    ctx.renderer.setClearColor(0x000000, 1);
    ctx.renderer.clear(true, true, true);
    ctx.renderer.render(ctx.scene, ctx.camera);
  }
}

/** Get the audio engine — used by usePlayback to start/stop audio on user gesture */
export function getAudioEngine(): IAudioEngine | null {
  return audio;
}

/** Get the audio engine as ToneAudioEngine for multi-track features */
export function getToneAudioEngine(): ToneAudioEngine | null {
  return audio instanceof ToneAudioEngine ? audio : null;
}
